import React, { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Button } from 'app/shared/button';
import { useTenant } from 'contexts/tenant';
import { useIssueService } from 'hooks/agents/issue';
import { useIssueReportService } from 'hooks/agents/issue-report';
import styles from './error-boundary.module.css';

const ISSUE_CATEGORY = 'bug';
const ISSUE_SEVERITY = 'critical';
const REPORT_DEDUPE_WINDOW_MS = 10000;

// Module-level so it survives the fresh mount/unmount an error boundary does on every catch.
const recently_reported_titles = new Map();

const canReportTitle = (title) => {
  const last_reported_at = recently_reported_titles.get(title);
  const now = Date.now();
  if (last_reported_at && now - last_reported_at < REPORT_DEDUPE_WINDOW_MS) return false;

  recently_reported_titles.set(title, now);
  return true;
};

export const ErrorFallback = ({ className, error, info }) => {
  const { tenant } = useTenant();
  const { searchIssues, createIssue } = useIssueService();
  const { createIssueReport } = useIssueReportService();
  const has_reported = useRef(false);

  useEffect(() => {
    const tenant_id = tenant?.id || tenant?._id;
    if (has_reported.current || !tenant_id) return;

    const title = (error?.message || 'Unhandled application error').trim().slice(0, 150);
    if (!canReportTitle(title)) return;
    has_reported.current = true;

    // silent: true — this is an automatic system report, not a user-initiated action, so we
    // skip the hooks' generic "Issue created"/"Unable to..." toasts and speak for it below.
    const reportError = async () => {
      const { issues } = await searchIssues(title, { silent: true });
      let issue = issues.find((existing) => existing.title === title);

      if (!issue) {
        const { issue: created_issue } = await createIssue(
          {
            title,
            description: error?.stack || null,
            category: ISSUE_CATEGORY,
            severity: ISSUE_SEVERITY,
            jira_key: null,
            jira_url: null
          },
          { silent: true }
        );
        issue = created_issue;
      }

      if (!issue) {
        console.error('ErrorBoundary: unable to file a system issue for', title);
        return;
      }

      const { report } = await createIssueReport(
        {
          issue_id: issue.id || issue._id,
          tenant: { id: tenant_id, name: tenant.name || '' },
          details: info?.componentStack?.trim() || undefined
        },
        { silent: true }
      );

      if (!report) {
        console.error('ErrorBoundary: unable to bind system issue to tenant', title);
        return;
      }

      toast.info('This is a system reported issue — our engineering team has been notified.');
    };

    reportError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);

  return (
    <div className={[styles.wrapper, className].filter(Boolean).join(' ')}>
      <div className={styles.card}>
        <h1 className={styles.heading}>Oops!</h1>
        <p className={styles.message}>That was unexpected... our engineers have been notified.</p>
        <Button
          className={styles.reloadButton}
          text="Reload page"
          onClick={() => window.location.reload()}
        />
      </div>
    </div>
  );
};
